import {expectEither, HttpClient, POST, useHttpClient} from '@raison/network-facade';
import {useMutation} from '@tanstack/react-query';
import {type} from 'arktype';
import {absurd} from 'fp-ts/lib/function';
import {UserInfoResource} from '~/processes/crypto-send-and-receive/model/useUserInfoResource';
import {OrderId} from '../model/TrTx';
import {OriginatorValues, participantIsCompany} from '../model/TrTxParticipant';

/**
 * https://conf.raison.digital/pages/viewpage.action?pageId=151355394
 */
export async function submitTrTxOriginator(
    http: HttpClient,
    orderId: OrderId,
    data: OriginatorValues,
    user: UserInfoResource
) {
    const body = {
        order_id: orderId,
        originator_type: data.participantType,
        originator_name: data.participantName,
        originator_country: data.participantCountry,
        originator_town: data.participantTown,
        originator_address: data.participantAddress,
        originator_legal_form: participantIsCompany(data, user) ? 'company' : 'individual',
        ...(data.walletType === 'vasp' && data.walletVasp
            ? {originator_wallet_type: 'vasp', originator_vasp_id: data.walletVasp.id}
            : {originator_wallet_type: 'unhosted'}),
    };

    return http('v1/travel_rule/process_receive_order', POST(body)).then(
        ...expectEither({
            200: null,
            400: type({
                violations: type({
                    message: 'string',
                    propertyPath: 'string',
                }).array(),
            }).pipe(v => {
                const fields = Object.fromEntries(v.violations.map(v => [v.propertyPath, v.message]));
                return {
                    walletVasp: fields.originatorVaspId,
                    walletType: fields.originatorWalletType,
                    participantType: fields.originatorType,
                    participantName: fields.originatorName,
                    participantCountry: fields.originatorCountry,
                    participantTown: fields.originatorTown,
                    participantAddress: fields.originatorAddress,
                };
            }),
        })
    );
}

export function useTrTxReceiverDataSubmit() {
    const http = useHttpClient();
    const {
        mutate: _mutate,
        mutateAsync,
        ...mutationState
    } = useMutation({
        mutationFn: async (data: {orderId: OrderId; values: OriginatorValues; user: UserInfoResource}) => {
            const response = await submitTrTxOriginator(http, data.orderId, data.values, data.user);
            switch (response.status) {
                case 400:
                    return {issues: response.json};
                case 200:
                    return {ok: true};
                case 'Abort':
                    return {ok: false};
                default:
                    return absurd<never>(response);
            }
        },
    });
    return {...mutationState, submitOriginator: mutateAsync};
}
